import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, KeyRound, Trash2, Loader2, Lock, LockOpen, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { erpApi, formatApiErrorDetail } from "@/lib/erpApi";
import { useErpAuth } from "@/context/ErpAuthContext";
import { ChangeOwnPasswordDialog } from "@/components/erp/ChangeOwnPassword";

const emptyForm = {
  username: "",
  email: "",
  password: "",
  nume: "",
  prenume: "",
  cnp: "",
  functie: "",
  phone: "",
  role: "",
  id_expiry: "",
  driver_licence_expiry: "",
  work_medicine_expiry: "",
  catg: "",
  numar_contract: "",
  angajat_de_la: "",
  norma_ore: 8,
  zile_co_anuale: 21,
  program_start: "08:00",
  program_sfarsit: "17:00",
  program_pauza: true,
  pauza_start: "12:00",
  pauza_sfarsit: "13:00",
  in_pontaj: true,
  pontaj_pana_la: "",
  active: true,
  fost_angajat: false,
};

const displayName = (e) => `${e.nume || ""} ${e.prenume || ""}`.trim() || e.name || e.email;

const fmtDate = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (isNaN(d)) return "-";
  return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const isExpired = (s) => {
  if (!s) return false;
  const d = new Date(s);
  return !isNaN(d) && d < new Date();
};

export default function AngajatiTab() {
  const { hasPerm, user: me } = useErpAuth();
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [selfPwOpen, setSelfPwOpen] = useState(false);

  const canCreate = hasPerm("angajati.creare");
  const canEdit = hasPerm("angajati.editare");
  const canDelete = hasPerm("angajati.stergere");
  const isAdmin = me?.role === "admin";
  const showActions = canEdit || canDelete;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, r] = await Promise.all([erpApi.get("/employees"), erpApi.get("/roles")]);
      setEmployees(e.data);
      setRoles(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roleLabel = (name) => {
    if (name === "admin") return "Administrator";
    const r = roles.find((x) => x.name === name);
    return r ? r.label : name;
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, role: roles.find((r) => r.name !== "admin")?.name || "" });
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      ...emptyForm,
      ...emp,
      functie: emp.functie || emp.position || "",
      password: "",
      norma_ore: emp.norma_ore || 8,
      zile_co_anuale: emp.zile_co_anuale || 21,
      program_start: emp.program_start || "08:00",
      program_sfarsit: emp.program_sfarsit || "17:00",
      program_pauza: emp.program_pauza !== false,
      pauza_start: emp.pauza_start || "12:00",
      pauza_sfarsit: emp.pauza_sfarsit || "13:00",
      in_pontaj: emp.in_pontaj !== false,
      pontaj_pana_la: emp.pontaj_pana_la || "",
      active: emp.active !== false,
      fost_angajat: emp.fost_angajat === true,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing && !form.password) {
      toast.error("Parola este obligatorie");
      return;
    }
    if (!editing && !form.email && !form.username) {
      toast.error("Adaugă un email sau un nume de utilizator");
      return;
    }
    if (!form.role) {
      toast.error("Selectează un rol");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { password, created_at, created_by, id, name, _id, permissions, ...payload } = form;
        await erpApi.put(`/employees/${editing.id}`, payload);
        toast.success("Angajat actualizat");
      } else {
        await erpApi.post("/employees", form);
        toast.success("Angajat creat");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleAcces = async (emp) => {
    try {
      await erpApi.patch(`/employees/${emp.id}/status`, { active: !emp.active });
      toast.success(emp.active ? "Acces blocat" : "Acces deblocat");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const openPasswordDialog = (emp) => {
    setPwdTarget(emp);
    setNewPwd("");
    setConfirmPwd("");
  };

  const submitPassword = async () => {
    if (newPwd.length < 6) {
      toast.error("Parola trebuie să aibă minim 6 caractere");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Parolele nu coincid");
      return;
    }
    setPwdSaving(true);
    try {
      await erpApi.post(`/employees/${pwdTarget.id}/reset-password`, { new_password: newPwd });
      toast.success(`Parolă schimbată pentru ${displayName(pwdTarget)}`);
      setPwdTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setPwdSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await erpApi.delete(`/employees/${deleteTarget.id}`);
      toast.success("Angajat șters");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const filtered = employees
    .filter((e) => (statusFilter === "active" ? !e.fost_angajat : e.fost_angajat))
    .filter((e) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        displayName(e).toLowerCase().includes(s) ||
        (e.username || "").toLowerCase().includes(s) ||
        (e.email || "").toLowerCase().includes(s) ||
        (e.functie || e.position || "").toLowerCase().includes(s)
      );
    })
    .sort((a, b) => (parseInt(a.numar_contract) || 999999) - (parseInt(b.numar_contract) || 999999));

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div className="space-y-4" data-testid="erp-angajati-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              data-testid="erp-filter-active"
              onClick={() => setStatusFilter("active")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === "active" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              Activi
            </button>
            <button
              data-testid="erp-filter-former"
              onClick={() => setStatusFilter("former")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === "former" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              Foști
            </button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="erp-employee-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută nume, utilizator, funcție..."
              className="pl-8 h-9 w-64"
            />
          </div>
        </div>
        {canCreate && (
          <Button
            onClick={openCreate}
            data-testid="erp-add-employee-button"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Adaugă angajat
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-2">Nr.</TableHead>
              <TableHead className="px-2">Angajat</TableHead>
              <TableHead className="px-2">Utilizator</TableHead>
              <TableHead className="px-2">Nume</TableHead>
              <TableHead className="px-2">Funcție</TableHead>
              <TableHead className="px-2">CNP</TableHead>
              <TableHead className="px-2">Rol</TableHead>
              <TableHead className="px-2">Normă</TableHead>
              <TableHead className="px-2">Program</TableHead>
              <TableHead className="px-2">Pontaj</TableHead>
              <TableHead className="px-2">ID</TableHead>
              <TableHead className="px-2">Permis</TableHead>
              <TableHead className="px-2">Med.</TableHead>
              <TableHead className="px-2">Cat.</TableHead>
              <TableHead className="px-2">Creat de</TableHead>
              {showActions && <TableHead className="px-2 text-right">Acțiuni</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((emp) => (
              <TableRow key={emp.id} data-testid={`erp-employee-row-${emp.id}`} className={emp.active === false ? "bg-slate-50" : ""}>
                <TableCell className="px-2 text-center text-xs font-medium">{emp.numar_contract || "-"}</TableCell>
                <TableCell className="px-2 text-xs text-slate-600">{fmtDate(emp.angajat_de_la)}</TableCell>
                <TableCell className="px-2 text-xs font-medium">{emp.username || "-"}</TableCell>
                <TableCell className="px-2 text-xs text-[#0B1424] font-semibold">{displayName(emp)}</TableCell>
                <TableCell className="px-2 text-xs text-slate-600">{emp.functie || emp.position || "-"}</TableCell>
                <TableCell className="px-2 text-xs text-slate-600">{emp.cnp || "-"}</TableCell>
                <TableCell className="px-2">
                  <Badge variant="secondary" className="font-medium text-xs">{roleLabel(emp.role)}</Badge>
                </TableCell>
                <TableCell className="px-2">
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">{emp.norma_ore || 8}h</span>
                </TableCell>
                <TableCell className="px-2 text-xs text-slate-600 whitespace-nowrap">
                  {(emp.program_start || "08:00")}-{(emp.program_sfarsit || "17:00")}
                </TableCell>
                <TableCell className="px-2">
                  {emp.role === "admin" ? (
                    <span className="text-xs text-slate-400">—</span>
                  ) : emp.in_pontaj === false ? (
                    <span className="text-xs text-slate-400">Exclus</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Inclus</span>
                  )}
                </TableCell>
                <TableCell className="px-2">
                  <span className={`text-xs ${isExpired(emp.id_expiry) ? "text-red-600 font-semibold" : "text-slate-600"}`}>{fmtDate(emp.id_expiry)}</span>
                </TableCell>
                <TableCell className="px-2">
                  <span className={`text-xs ${isExpired(emp.driver_licence_expiry) ? "text-red-600 font-semibold" : "text-slate-600"}`}>{fmtDate(emp.driver_licence_expiry)}</span>
                </TableCell>
                <TableCell className="px-2">
                  <span className={`text-xs ${isExpired(emp.work_medicine_expiry) ? "text-red-600 font-semibold" : "text-slate-600"}`}>{fmtDate(emp.work_medicine_expiry)}</span>
                </TableCell>
                <TableCell className="px-2 text-xs font-medium uppercase">{emp.catg || "-"}</TableCell>
                <TableCell className="px-2 text-xs text-slate-500">{emp.created_by || "-"}</TableCell>
                {showActions && (
                <TableCell className="px-2 text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Editează" data-testid={`erp-edit-employee-${emp.id}`} onClick={() => openEdit(emp)}>
                          <Pencil className="w-3.5 h-3.5 text-[#2563EB]" />
                        </Button>
                        {emp.id !== me?.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title={isAdmin ? (emp.active ? "Blochează acces" : "Deblochează acces") : "Doar adminul poate bloca accesul"}
                          data-testid={`erp-toggle-employee-${emp.id}`}
                          onClick={() => (isAdmin ? toggleAcces(emp) : toast.info("Doar administratorul poate bloca sau debloca accesul utilizatorilor. Pentru această modificare solicitați suportul Admin."))}
                        >
                          {emp.active ? <LockOpen className="w-3.5 h-3.5 text-emerald-600" /> : <Lock className="w-3.5 h-3.5 text-red-500" />}
                        </Button>
                        )}
                        {emp.id !== me?.id && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" title={isAdmin ? "Schimbă parola" : "Doar adminul poate modifica parola altora"} data-testid={`erp-change-password-${emp.id}`} onClick={() => (isAdmin ? openPasswordDialog(emp) : toast.info("Doar administratorul poate modifica parola altor utilizatori. Pentru această modificare solicitați suportul Admin."))}>
                          <KeyRound className="w-3.5 h-3.5 text-amber-500" />
                        </Button>
                        )}
                      </>
                    )}
                    {emp.id === me?.id && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Schimbă parola mea" data-testid="erp-self-change-password" onClick={() => setSelfPwOpen(true)}>
                        <KeyRound className="w-3.5 h-3.5 text-amber-500" />
                      </Button>
                    )}
                    {canDelete && emp.id !== me?.id ? (
                      <Button variant="ghost" size="icon" className="h-7 w-7" title={isAdmin ? "Șterge" : "Doar adminul poate șterge"} data-testid={`erp-delete-employee-${emp.id}`} onClick={() => (isAdmin ? setDeleteTarget(emp) : toast.info("Doar administratorul poate șterge utilizatori. Pentru această modificare solicitați suportul Admin."))}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    ) : (
                      canDelete && <span className="inline-block w-7 h-7" aria-hidden />
                    )}
                  </div>
                </TableCell>
                )}
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={showActions ? 16 : 15} className="text-center py-10 text-slate-400">
                  {statusFilter === "active" ? "Niciun angajat activ." : "Niciun fost angajat."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editează angajat" : "Angajat nou"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Utilizator</Label>
                <Input data-testid="erp-form-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="ex: ipopescu" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-semibold">Email (opțional — pentru login)</Label>
                <Input type="email" data-testid="erp-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            {!editing && (
              <div>
                <Label className="font-semibold">Parolă</Label>
                <Input type="text" data-testid="erp-form-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 6 caractere" className="mt-1.5" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Nume</Label>
                <Input data-testid="erp-form-nume" value={form.nume} onChange={(e) => setForm({ ...form, nume: e.target.value })} placeholder="ex: Popescu" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-semibold">Prenume</Label>
                <Input data-testid="erp-form-prenume" value={form.prenume} onChange={(e) => setForm({ ...form, prenume: e.target.value })} placeholder="ex: Ion" className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">CNP</Label>
                <Input data-testid="erp-form-cnp" value={form.cnp} onChange={(e) => setForm({ ...form, cnp: e.target.value })} placeholder="1234567890123" className="mt-1.5" />
              </div>
              <div>
                <Label className="font-semibold">Funcție</Label>
                <Input data-testid="erp-form-functie" value={form.functie} onChange={(e) => setForm({ ...form, functie: e.target.value })} placeholder="Mecanic, Recepție..." className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="erp-form-role" className="mt-1.5">
                    <SelectValue placeholder="Alege rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-semibold">Telefon</Label>
                <Input data-testid="erp-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm mb-3 text-slate-700">Documente (dată expirare)</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Carte Identitate</Label>
                  <Input type="date" data-testid="erp-form-id-expiry" value={form.id_expiry || ""} onChange={(e) => setForm({ ...form, id_expiry: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Permis conducere</Label>
                  <Input type="date" data-testid="erp-form-permis" value={form.driver_licence_expiry || ""} onChange={(e) => setForm({ ...form, driver_licence_expiry: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Medicina muncii</Label>
                  <Input type="date" data-testid="erp-form-medicina" value={form.work_medicine_expiry || ""} onChange={(e) => setForm({ ...form, work_medicine_expiry: e.target.value })} className="mt-1" />
                </div>
              </div>
              <div className="mt-3 w-1/3 pr-2">
                <Label className="text-xs">Categorie permis</Label>
                <Input data-testid="erp-form-catg" value={form.catg} onChange={(e) => setForm({ ...form, catg: e.target.value.toUpperCase() })} placeholder="B, C, CE..." className="mt-1 uppercase" />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nr. contract</Label>
                  <Input data-testid="erp-form-contract" value={form.numar_contract} onChange={(e) => setForm({ ...form, numar_contract: e.target.value })} placeholder="ex: 1, 2, 3..." className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Data angajării</Label>
                  <Input type="date" data-testid="erp-form-angajat-de-la" value={form.angajat_de_la || ""} onChange={(e) => setForm({ ...form, angajat_de_la: e.target.value })} className="mt-1" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">Nr. contract determină ordinea în listă.</p>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm mb-3 text-slate-700">Normă și program de lucru</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Normă zilnică</Label>
                  <div className="flex gap-2 mt-1">
                    {[2, 4, 6, 8].map((ore) => (
                      <Button
                        key={ore}
                        type="button"
                        variant={form.norma_ore === ore ? "default" : "outline"}
                        size="sm"
                        data-testid={`erp-form-norma-${ore}`}
                        onClick={() => setForm({ ...form, norma_ore: ore })}
                        className={`flex-1 ${form.norma_ore === ore ? "bg-[#2563EB] hover:bg-[#1D4ED8]" : ""}`}
                      >
                        {ore}h
                      </Button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Zile CO anuale</Label>
                  <Input type="number" min="0" max="50" data-testid="erp-form-zile-co" value={form.zile_co_anuale} onChange={(e) => setForm({ ...form, zile_co_anuale: parseInt(e.target.value) || 0 })} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-xs">Program start</Label>
                  <Input type="time" data-testid="erp-form-program-start" value={form.program_start} onChange={(e) => setForm({ ...form, program_start: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Program sfârșit</Label>
                  <Input type="time" data-testid="erp-form-program-sfarsit" value={form.program_sfarsit} onChange={(e) => setForm({ ...form, program_sfarsit: e.target.value })} className="mt-1" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Switch checked={form.program_pauza} onCheckedChange={(v) => setForm({ ...form, program_pauza: v })} data-testid="erp-form-pauza" className="data-[state=checked]:bg-[#2563EB]" />
                <Label className="text-sm">Include pauză de masă</Label>
              </div>
              {form.program_pauza && (
                <div className="grid grid-cols-2 gap-3 mt-3 pl-6">
                  <div>
                    <Label className="text-xs">Pauză de la</Label>
                    <Input type="time" data-testid="erp-form-pauza-start" value={form.pauza_start} onChange={(e) => setForm({ ...form, pauza_start: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Pauză până la</Label>
                    <Input type="time" data-testid="erp-form-pauza-sfarsit" value={form.pauza_sfarsit} onChange={(e) => setForm({ ...form, pauza_sfarsit: e.target.value })} className="mt-1" />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center gap-2">
                <Switch checked={form.in_pontaj} onCheckedChange={(v) => setForm({ ...form, in_pontaj: v })} data-testid="erp-form-in-pontaj" className="data-[state=checked]:bg-[#2563EB]" />
                <Label className="font-semibold">Inclus în pontaj</Label>
              </div>
              {form.in_pontaj && (
                <div className="mt-3 w-1/2 pr-2">
                  <Label className="text-xs">În pontaj până la (data ieșirii — opțional)</Label>
                  <Input type="date" data-testid="erp-form-pontaj-pana-la" value={form.pontaj_pana_la || ""} onChange={(e) => setForm({ ...form, pontaj_pana_la: e.target.value })} className="mt-1" />
                </div>
              )}
            </div>

            <div className="border-t pt-4 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="erp-form-active" className="data-[state=checked]:bg-[#2563EB]" />
                <Label className="font-semibold">Acces activ</Label>
              </div>
              {editing && (
                <div className="flex items-center gap-2">
                  <Switch checked={form.fost_angajat} onCheckedChange={(v) => setForm({ ...form, fost_angajat: v })} data-testid="erp-form-fost" className="data-[state=checked]:bg-slate-500" />
                  <Label className="font-semibold">Fost angajat</Label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Anulează</Button>
            <Button onClick={save} disabled={saving} data-testid="erp-save-employee-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwdTarget} onOpenChange={(o) => !o && setPwdTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schimbă parola</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">
              Setezi o parolă nouă pentru <span className="font-semibold text-[#0B1424]">{pwdTarget && displayName(pwdTarget)}</span> ({pwdTarget?.email}).
            </p>
            <div>
              <Label className="font-semibold">Parolă nouă</Label>
              <Input
                type="text"
                data-testid="erp-change-password-input"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="min. 6 caractere"
                className="mt-1.5"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label className="font-semibold">Confirmă parola</Label>
              <Input
                type="text"
                data-testid="erp-change-password-confirm"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="repetă parola"
                className="mt-1.5"
                autoComplete="new-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdTarget(null)}>Anulează</Button>
            <Button onClick={submitPassword} disabled={pwdSaving} data-testid="erp-change-password-save" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schimbă parola"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChangeOwnPasswordDialog open={selfPwOpen} onOpenChange={setSelfPwOpen} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergi acest angajat?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && displayName(deleteTarget)} ({deleteTarget?.email}) va fi șters definitiv.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="erp-confirm-delete-employee" className="bg-[#DC2626] hover:bg-[#B91C1C]">
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
